<template>
  <div class="list-container">
    <div class="list-header">
      <h1>Publishing Admin</h1>
      <router-link to="/admin/publishing/new" class="important-button big">
        <font-awesome-icon icon="plus" /> Publish Video
      </router-link>
    </div>

    <div class="stats-panel">
      <div class="stats-container">
        <div class="stat-box">
          <div class="stat-title">TV</div>
          <div class="stat-value">{{ videos.length }}</div>
          <div class="stat-description">Total Videos</div>
        </div>
        <div class="stat-box">
          <div class="stat-title">PV</div>
          <div class="stat-value">0</div>
          <div class="stat-description">Published Videos</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'sv' }"
          @click="toggleFilter('sv')"
        >
          <div class="stat-title">SV</div>
          <div class="stat-value">{{ sharedVideos }}</div>
          <div class="stat-description">Shared Videos</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'msv' }"
          @click="toggleFilter('msv')"
        >
          <div class="stat-title">MSV</div>
          <div class="stat-value">{{ monthlySharedVideos }}</div>
          <div class="stat-description">Monthly Shared Videos</div>
        </div>
        <div 
          class="stat-box clickable" 
          :class="{ 'active': activeFilter === 'wsv' }"
          @click="toggleFilter('wsv')"
        >
          <div class="stat-title">WSV</div>
          <div class="stat-value">{{ weeklySharedVideos }}</div>
          <div class="stat-description">Weekly Shared Videos</div>
        </div>
      </div>
      <div v-if="activeFilter" class="filter-indicator">
        <span>Filtering by: {{ getFilterName(activeFilter) }}</span>
        <button class="clear-filter" @click="clearFilter">Clear Filter</button>
      </div>
    </div>

    <VideoList
      ref="videoListRef"
      :show-channels="true"
      :filter="activeFilterFunction"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { FontAwesomeIcon } from '@/scripts/icons';
import { isSuperuser } from '@/scripts/permissions';
import { getAllVideos, videoKeys } from '@/scripts/api/videoApi';
import { handleApiError } from '@/scripts/shared-utils';
import VideoList from '@/components/VideoList.vue';

const activeFilter = ref(null);
const videoListRef = ref(null);

// Fetch videos using Vue Query
const { data: videos } = useQuery({
  queryKey: videoKeys.getAllVideos(),
  queryFn: getAllVideos,
  enabled: isSuperuser(),
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching videos'),
});

// Calculate time thresholds once for all computed properties
const getTimeThresholds = () => {
  const now = new Date();
  const monthAgo = new Date(now);
  monthAgo.setDate(now.getDate() - 28); // 28 days for monthly metrics
  
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7); // 7 days for weekly metrics
  
  return { monthAgo, weekAgo };
};

// Filter functions for each metric
const isSharedVideo = (video) => {
  return video.share_links && video.share_links.length > 0;
};

const isMonthlySharedVideo = (video) => {
  const { monthAgo } = getTimeThresholds();
  if (!isSharedVideo(video)) return false;
  const createdAtDate = new Date(video.share_links[0].created_at);
  return createdAtDate >= monthAgo;
};

const isWeeklySharedVideo = (video) => {
  const { weekAgo } = getTimeThresholds();
  if (!isSharedVideo(video)) return false;
  const createdAtDate = new Date(video.share_links[0].created_at);
  return createdAtDate >= weekAgo;
};

// Computed properties using the filter functions
const sharedVideos = computed(() => {
  return videos.value.filter(isSharedVideo).length;
});

const monthlySharedVideos = computed(() => {
  return videos.value.filter(isMonthlySharedVideo).length;
});

const weeklySharedVideos = computed(() => {
  return videos.value.filter(isWeeklySharedVideo).length;
});

// Active filter function based on selected filter
const activeFilterFunction = computed(() => {
  if (!activeFilter.value) return null;
  
  switch (activeFilter.value) {
    case 'sv':
      return isSharedVideo;
    case 'msv':
      return isMonthlySharedVideo;
    case 'wsv':
      return isWeeklySharedVideo;
    default:
      return null;
  }
});

// Toggle filter when stat box is clicked
const toggleFilter = (filter) => {
  if (activeFilter.value === filter) {
    activeFilter.value = null;
  } else {
    activeFilter.value = filter;
  }
};

// Clear active filter
const clearFilter = () => {
  activeFilter.value = null;
};

// Get human-readable filter name
const getFilterName = (filter) => {
  switch (filter) {
    case 'sv': return 'Shared Videos';
    case 'msv': return 'Monthly Shared Videos';
    case 'wsv': return 'Weekly Shared Videos';
    default: return '';
  }
};
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/shared-stats.css';
</style>
